import { useEffect, useRef, useState, useCallback } from "react";
import { ArrowRight } from "lucide-react";
import { useMobileViewport } from "@/hooks/useMobileViewport";
import SiteNavbar from "@/components/SiteNavbar";

function ParticleCanvas() {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    let width = 0;
    let height = 0;
    let dpr = Math.min(window.devicePixelRatio || 1, 2);
    let visible = true;

    // Fewer particles for better frame budget alongside scroll animations.
    const PARTICLE_COUNT = 40;
    const LINK_DIST = 120;
    // Pre-build rgb strings once — avoids template literal allocation every frame.
    const COLORS = [
      { fill: "250,172,0", line: "rgba(250,172,0," },
      { fill: "151,216,196", line: "rgba(151,216,196," },
      { fill: "185,216,255", line: "rgba(185,216,255," },
      { fill: "217,204,255", line: "rgba(217,204,255," },
      { fill: "255,216,191", line: "rgba(255,216,191," },
    ];
    const particles = [];

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const init = () => {
      particles.length = 0;
      for (let i = 0; i < PARTICLE_COUNT; i += 1) {
        const c = COLORS[i % COLORS.length];
        particles.push({
          x: Math.random() * width,
          y: Math.random() * height,
          vx: (Math.random() - 0.5) * 0.18,
          vy: (Math.random() - 0.5) * 0.18,
          r: Math.random() * 1.4 + 0.6,
          color: c,
          tw: Math.random() * Math.PI * 2,
        });
      }
    };

    const draw = () => {
      rafRef.current = requestAnimationFrame(draw);
      // Pause drawing when canvas is scrolled out of view to free frame budget.
      if (!visible) return;

      ctx.clearRect(0, 0, width, height);

      // Draw all particles without shadowBlur — shadow is the single most
      // expensive canvas operation and isn't worth it for tiny dots.
      for (let i = 0; i < particles.length; i += 1) {
        const p = particles[i];
        p.x += p.vx;
        p.y += p.vy;
        p.tw += 0.02;
        if (p.x < -10) p.x = width + 10;
        if (p.x > width + 10) p.x = -10;
        if (p.y < -10) p.y = height + 10;
        if (p.y > height + 10) p.y = -10;
        const alpha = 0.55 + Math.sin(p.tw) * 0.25;
        ctx.beginPath();
        ctx.fillStyle = `rgba(${p.color.fill},${alpha.toFixed(2)})`;
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }

      // Connecting lines — batched into a single strokeStyle group per opacity
      // level to reduce state changes.
      for (let i = 0; i < particles.length; i += 1) {
        const a = particles[i];
        for (let j = i + 1; j < particles.length; j += 1) {
          const b = particles[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const dist2 = dx * dx + dy * dy;
          if (dist2 < LINK_DIST * LINK_DIST) {
            const dist = Math.sqrt(dist2);
            const op = (1 - dist / LINK_DIST) * 0.15;
            ctx.beginPath();
            ctx.strokeStyle = `rgba(180,190,220,${op.toFixed(2)})`;
            ctx.lineWidth = 0.6;
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }
    };

    resize();
    init();
    draw();

    // Pause animation while the hero canvas is off-screen (user has scrolled
    // past the hero). This frees ~3-4ms of frame budget for scroll animations.
    const observer = new IntersectionObserver(
      (entries) => {
        visible = entries[0].isIntersecting;
      },
      { threshold: 0 },
    );
    observer.observe(canvas);

    const onResize = () => {
      resize();
      init();
    };
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
      observer.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return <canvas ref={canvasRef} className="sp-canvas" aria-hidden="true" />;
}

const CARDS = [
  {
    id: "live_class_card",
    title: "Live learning in progress",
    metric: "1:1",
    label: "Expert teacher support",
    tag: "Live Now",
    tagDotClass: "sp-tag-dot",
    video:
      "https://asset.sparkl.me/pb/poc-folder/OwWakZHY0EnZ5dAXkO9nXlK9K0W6y7XcDG4hHdW8.mp4",
    alt: "Animated soft-ui 3D video showing live class support with a 3D character",
    poster: "live",
  },
  {
    id: "ai_support_card",
    title: "Support beyond class",
    metric: "AI",
    label: "Guided doubt help",
    tag: "AI Tutor",
    tagDotClass: "sp-tag-dot",
    video:
      "https://asset.sparkl.me/pb/poc-folder/wCO2RIxE1InK9w5xxGspqTshopndd8WM4QPRQKjn.mp4",
    alt: "Animated soft-ui 3D video showing AI tutor support",
    poster: "ai",
  },
  {
    id: "growth_report_card",
    title: "Improvement over time",
    metric: "+24%",
    label: "Concept clarity gain",
    tag: "Growth",
    tagDotClass: "sp-tag-dot sp-tag-dot--gold",
    video: "https://asset.sparkl.me/pb/assets/video/growth_video.mp4",
    alt: "Animated soft-ui 3D video showing academic growth report",
    poster: "growth",
  },
];

function CardPoster({ kind }) {
  // Soft-UI matte 3D-feel SVG fallback per card type. Lives BEHIND the video.
  // When the video can decode, it covers this completely. Otherwise, this
  // remains visible and matches the Sparkl Atmosphere style.
  const common = {
    width: "100%",
    height: "100%",
    viewBox: "0 0 480 270",
    preserveAspectRatio: "xMidYMid slice",
    "aria-hidden": "true",
  };
  const bg = (
    <>
      <defs>
        <radialGradient id={`bg-${kind}`} cx="20%" cy="18%" r="80%">
          <stop offset="0%" stopColor="#FFF7E2" stopOpacity="0.9" />
          <stop offset="55%" stopColor="#FFFFFF" stopOpacity="1" />
          <stop offset="100%" stopColor="#FCFBF8" stopOpacity="1" />
        </radialGradient>
        <radialGradient id={`accent-${kind}`} cx="80%" cy="82%" r="70%">
          <stop offset="0%" stopColor="#DDF4E8" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect x="0" y="0" width="480" height="270" fill={`url(#bg-${kind})`} />
      <rect
        x="0"
        y="0"
        width="480"
        height="270"
        fill={`url(#accent-${kind})`}
      />
    </>
  );

  if (kind === "progress") {
    return (
      <svg {...common}>
        {bg}
        <defs>
          <linearGradient id="ring-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#F8CC6B" />
            <stop offset="100%" stopColor="#FAAC00" />
          </linearGradient>
        </defs>
        <circle
          cx="240"
          cy="130"
          r="68"
          fill="none"
          stroke="#F2EEE3"
          strokeWidth="12"
        />
        <circle
          cx="240"
          cy="130"
          r="68"
          fill="none"
          stroke="url(#ring-grad)"
          strokeWidth="12"
          strokeLinecap="round"
          strokeDasharray="427"
          strokeDashoffset="86"
          transform="rotate(-90 240 130)"
        />
        {[0, 1, 2, 3, 4].map((i) => {
          const angle = (i * 70 - 110) * (Math.PI / 180);
          const x = 240 + Math.cos(angle) * 110;
          const y = 130 + Math.sin(angle) * 110;
          return (
            <circle
              key={i}
              cx={x}
              cy={y}
              r="6"
              fill={i < 4 ? "#97D8C4" : "#FFFFFF"}
              stroke="#E6EFEC"
              strokeWidth="1"
            />
          );
        })}
        <circle cx="240" cy="130" r="42" fill="#FFFFFF" />
        <text
          x="240"
          y="138"
          textAnchor="middle"
          fontFamily="Manrope, sans-serif"
          fontWeight="800"
          fontSize="26"
          fill="#1E2430"
          letterSpacing="-1"
        >
          82%
        </text>
      </svg>
    );
  }

  if (kind === "path") {
    return (
      <svg {...common}>
        {bg}
        <path
          d="M40 200 Q 130 50 240 130 T 450 60"
          fill="none"
          stroke="#E2DCCC"
          strokeWidth="2.5"
          strokeDasharray="6 8"
        />
        {[
          { x: 40, y: 200, c: "#97D8C4" },
          { x: 145, y: 110, c: "#97D8C4" },
          { x: 245, y: 130, c: "#FAAC00", r: 16 },
          { x: 350, y: 80, c: "#D9CCFF" },
          { x: 445, y: 60, c: "#FFD8BF" },
        ].map((n, i) => (
          <g key={i}>
            <circle
              cx={n.x}
              cy={n.y}
              r={(n.r || 12) + 6}
              fill="#FFFFFF"
              opacity="0.8"
            />
            <circle cx={n.x} cy={n.y} r={n.r || 12} fill={n.c} />
          </g>
        ))}
      </svg>
    );
  }

  if (kind === "live") {
    return (
      <svg {...common}>
        {bg}
        {/* main tile */}
        <rect
          x="60"
          y="34"
          width="260"
          height="200"
          rx="20"
          fill="#FFFDF8"
          stroke="#F0EBDF"
        />
        {/* avatar */}
        <circle cx="190" cy="138" r="44" fill="#FFE4D2" />
        <circle cx="190" cy="124" r="18" fill="#FFD0B0" />
        <path
          d="M156 162 Q190 184 224 162 L224 200 L156 200 Z"
          fill="#FFD0B0"
        />
        {/* live dot */}
        <circle cx="84" cy="60" r="5" fill="#FAAC00" />
        <text
          x="94"
          y="64"
          fontFamily="Manrope"
          fontWeight="700"
          fontSize="10"
          fill="#1E2430"
          letterSpacing="1"
        >
          LIVE
        </text>
        {/* student tile */}
        <rect
          x="338"
          y="60"
          width="98"
          height="74"
          rx="14"
          fill="#FFFFFF"
          stroke="#EFEAE0"
        />
        <circle cx="387" cy="92" r="14" fill="#B9D8FF" />
        <rect x="350" y="116" width="74" height="5" rx="2.5" fill="#EAF1FB" />
        {/* secondary panel */}
        <rect x="338" y="148" width="98" height="86" rx="14" fill="#F6F8FB" />
        <rect x="350" y="170" width="60" height="5" rx="2.5" fill="#DCE4F2" />
        <rect x="350" y="184" width="44" height="5" rx="2.5" fill="#E6ECF7" />
        <circle cx="354" cy="216" r="6" fill="#97D8C4" />
        <rect x="368" y="213" width="44" height="6" rx="3" fill="#E9F4EF" />
      </svg>
    );
  }

  if (kind === "ai") {
    return (
      <svg {...common}>
        {bg}
        {/* assistant bubble */}
        <rect
          x="60"
          y="44"
          width="320"
          height="56"
          rx="18"
          fill="#FFFDF8"
          stroke="#EFEAE0"
        />
        <circle cx="92" cy="72" r="14" fill="#97D8C4" />
        <rect x="116" y="62" width="220" height="7" rx="3.5" fill="#E9F4EF" />
        <rect x="116" y="78" width="160" height="7" rx="3.5" fill="#F0EFEB" />
        {/* response panels */}
        <rect x="60" y="114" width="240" height="46" rx="14" fill="#F6F8FB" />
        <rect x="80" y="130" width="140" height="6" rx="3" fill="#DCE4F2" />
        <rect x="80" y="144" width="90" height="6" rx="3" fill="#E6ECF7" />
        <rect x="60" y="172" width="280" height="46" rx="14" fill="#FFF8E6" />
        <rect x="80" y="188" width="180" height="6" rx="3" fill="#F2DCA0" />
        <rect x="80" y="202" width="110" height="6" rx="3" fill="#FAEAC1" />
        {/* sparkle accent */}
        <circle cx="400" cy="180" r="22" fill="#FAAC00" opacity="0.14" />
        <circle cx="400" cy="180" r="9" fill="#FAAC00" />
      </svg>
    );
  }

  if (kind === "growth") {
    return (
      <svg {...common}>
        {bg}
        <rect
          x="30"
          y="34"
          width="420"
          height="208"
          rx="20"
          fill="#FFFDF8"
          stroke="#F0EBDF"
        />
        {/* grid */}
        {[80, 120, 160, 200].map((y) => (
          <line
            key={y}
            x1="60"
            y1={y}
            x2="420"
            y2={y}
            stroke="#F4EFE3"
            strokeDasharray="4 6"
          />
        ))}
        <defs>
          <linearGradient id="line-grad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#97D8C4" />
            <stop offset="100%" stopColor="#FAAC00" />
          </linearGradient>
          <linearGradient id="fill-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#FAAC00" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#FAAC00" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path
          d="M60 200 L130 180 L200 168 L270 148 L340 110 L420 70 L420 220 L60 220 Z"
          fill="url(#fill-grad)"
        />
        <path
          d="M60 200 L130 180 L200 168 L270 148 L340 110 L420 70"
          fill="none"
          stroke="url(#line-grad)"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle
          cx="340"
          cy="110"
          r="8"
          fill="#FFFFFF"
          stroke="#FAAC00"
          strokeWidth="3"
        />
      </svg>
    );
  }
  return <svg {...common}>{bg}</svg>;
}

// Hero curriculum pills → Laravel/stream pages (labels stay as displayed)
const CURRICULA = [
  { label: "IB", href: "/ib" },
  { label: "IGCSE", href: "/cie" },
  { label: "SAT", href: "/sat" },
  { label: "ACT", href: "/act" },
  { label: "AP", href: "/ap" },
  { label: "CBSE", href: "/cbse" },
  { label: "JEE", href: "/jee" },
  { label: "NEET", href: "/neet" },
];

export default function HeroSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const isMobile = useMobileViewport();
  const videoRefs = useRef([]);
  const activeIndexRef = useRef(0);
  const heroVisibleRef = useRef(true);
  const heroFadeRef = useRef(null);
  const heroSectionRef = useRef(null);

  activeIndexRef.current = activeIndex;

  const syncHeroVideos = useCallback(({ restartActive = false } = {}) => {
    videoRefs.current.forEach((v, i) => {
      if (!v) return;
      if (i === activeIndexRef.current && heroVisibleRef.current) {
        try {
          if (restartActive) v.currentTime = 0;
          v.playbackRate = 1;
          const p = v.play();
          if (p && typeof p.catch === "function") p.catch(() => {});
        } catch {
          /* noop */
        }
        return;
      }
      if (i !== activeIndexRef.current) {
        try {
          v.pause();
          v.currentTime = 0;
        } catch {
          /* noop */
        }
      }
    });
  }, []);

  /* Pause only when hero scrolls off-screen; resume when it returns. */
  useEffect(() => {
    const root = heroSectionRef.current;
    if (!root) return undefined;

    const syncVisibility = (visible) => {
      heroVisibleRef.current = visible;
      if (visible) {
        syncHeroVideos();
        return;
      }
      videoRefs.current.forEach((v) => {
        try {
          v?.pause();
        } catch {
          /* noop */
        }
      });
    };

    if (typeof IntersectionObserver === "undefined") {
      syncVisibility(true);
      return undefined;
    }

    const io = new IntersectionObserver(
      ([entry]) => syncVisibility(entry.isIntersecting),
      { threshold: 0.01 },
    );
    io.observe(root);
    return () => io.disconnect();
  }, [syncHeroVideos]);

  // Smooth fade-out + slight upward drift as the hero scrolls past (desktop
  // only). Mobile keeps opacity 1 so the hero does not disappear while scrolling.
  // Desktop: continuous RAF fade while scrolling past hero. Mobile: static (native scroll).
  useEffect(() => {
    const sec = heroFadeRef.current;
    if (!sec) return undefined;

    const mq = window.matchMedia("(max-width: 767px)");
    const applyMobileFade = () => {
      sec.style.opacity = "1";
      sec.style.transform = "translate3d(0, 0, 0)";
    };

    if (mq.matches) {
      applyMobileFade();
      const onMqChange = (e) => {
        if (e.matches) applyMobileFade();
      };
      mq.addEventListener?.("change", onMqChange);
      mq.addListener?.(onMqChange);
      return () => {
        mq.removeEventListener?.("change", onMqChange);
        mq.removeListener?.(onMqChange);
      };
    }

    let rafId = 0;
    let lastT = -1;

    const loop = () => {
      rafId = requestAnimationFrame(loop);

      const rect = sec.getBoundingClientRect();
      // Hero is completely scrolled past — nothing to update.
      if (rect.bottom < 0) return;

      const vh = window.innerHeight || 1;
      // Round to 3 dp so micro-float drift doesn't trigger pointless writes.
      const t =
        Math.round(Math.min(1, Math.max(0, -rect.top / vh)) * 1000) / 1000;
      if (t === lastT) return;
      lastT = t;

      sec.style.opacity = String(1 - t);
      sec.style.transform = `translate3d(0, ${-t * 40}px, 0)`;
    };

    rafId = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafId);
  }, []);

  const advance = useCallback(() => {
    setActiveIndex((i) => (i + 1) % CARDS.length);
  }, []);

  // Sequence 1 → 2 → 3 → 1 on both web and mobile.
  useEffect(() => {
    syncHeroVideos({ restartActive: true });
  }, [activeIndex, syncHeroVideos]);

  const handleVideoEnded = useCallback(
    (i) => () => {
      if (i !== activeIndexRef.current) return;
      advance();
    },
    [advance],
  );

  const blockVideoInteraction = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const resumeActiveVideo = useCallback((video) => {
    if (!video || !heroVisibleRef.current) return;
    if (video.paused && !video.ended) {
      const p = video.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    }
  }, []);

  const handleVideoPause = useCallback(
    (i) => (e) => {
      if (i !== activeIndexRef.current) return;
      resumeActiveVideo(e.currentTarget);
    },
    [resumeActiveVideo],
  );

  // Keep the active clip running if the browser pauses it without user intent.
  useEffect(() => {
    if (!heroVisibleRef.current) return undefined;

    const tick = () => {
      resumeActiveVideo(videoRefs.current[activeIndexRef.current]);
    };

    const id = window.setInterval(tick, 400);
    return () => window.clearInterval(id);
  }, [activeIndex, resumeActiveVideo]);

  return (
    <>
      <SiteNavbar />

      <section
        ref={heroSectionRef}
        id="hero"
        className="sp-atmosphere"
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
        data-testid="hero-section"
      >
        {/* Ambient orbs / pathways / canvas removed for clean white background */}

        {/* Hero content — fade scope excludes fixed nav */}
        <div
          ref={heroFadeRef}
          className="sp-hero-fade-scope"
          style={{
            flex: "1 1 auto",
            minHeight: 0,
            ...(isMobile ? {} : { willChange: "opacity, transform" }),
          }}
        >
        <div
          style={{
            width: "min(1320px, calc(100% - 32px))",
            margin: "0 auto",
            padding: "72px 8px 96px",
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.65fr)",
            gap: "24px",
            alignItems: "center",
          }}
          className="sp-hero-grid"
          data-testid="hero-grid"
        >
          {/* Left text stack */}
          <div style={{ maxWidth: 620 }} data-testid="hero-text-stack">
            <h1
              className="sp-display sp-anim"
              style={{
                animationDelay: "260ms",
                fontSize: "clamp(48px, 6.6vw, 88px)",
                margin: "0 0 22px",
                color: "#1e2430",
              }}
              data-testid="hero-headline"
            >
              Learning Made
              <br />
              <span className="sp-grad-word -ml-4">Personal</span>
            </h1>

            <p
              className="sp-body sp-anim"
              style={{
                animationDelay: "440ms",
                fontSize: 18,
                maxWidth: 560,
                margin: "0 0 30px",
              }}
              data-testid="hero-supporting-line"
            >
              1-on-1 Live Classes with{" "}
              <span className="sp-hero-hl">Expert Teachers</span> paired with an{" "}
              <span className="sp-hero-hl">AI Tutor</span> that personalises
              every session to your child&rsquo;s learning path.
            </p>


            <div
              className="sp-anim sp-curriculum-chips"
              style={{
                animationDelay: "620ms",
                marginBottom: 32,
              }}
              data-testid="hero-curriculum-chips"
            >
              {CURRICULA.map(({ label, href }) => (
                <a
                  key={label}
                  href={href}
                  className="sp-chip"
                  data-testid={`curriculum-chip-${label.toLowerCase()}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {label}
                </a>
              ))}
            </div>

            {/* CTA row */}
            <div
              className="sp-anim"
              style={{
                animationDelay: "780ms",
                display: "flex",
                flexWrap: "wrap",
                gap: 14,
              }}
              data-testid="hero-cta-row"
            >
              <a
                href="/register"
                className="sp-btn sp-btn-primary registerLink"
                data-register-ref=""
                data-testid="cta-book-consultation"
                id="home-register-consult-hero"
                target="_blank"
                rel="noopener noreferrer"
              >
                Book a Free Consultation
                <ArrowRight size={17} strokeWidth={2.2} />
              </a>
              <a
                href="#programs"
                className="sp-btn sp-btn-secondary"
                data-testid="cta-explore-programs"
              >
                Explore Programs
              </a>
            </div>

            {/* Trust line removed per request */}
          </div>

          {/* Right showcase — single video on white with floating capsules */}
          <div
            className="sp-anim"
            style={{
              animationDelay: "560ms",
              display: "flex",
              justifyContent: "flex-end",
            }}
            data-testid="hero-stack-wrapper"
          >
            <div
              className="sp-showcase sp-showcase--ambient"
              data-testid="hero-card-stack"
              aria-hidden="true"
            >
              {CARDS.map((card, i) => (
                <article
                  key={card.id}
                  className={`sp-show ${i === activeIndex ? "is-active" : ""}`}
                  data-testid={`stack-card-${card.id}`}
                  aria-hidden={i !== activeIndex}
                >
                  <div className="sp-show-media">
                    {!isMobile && (
                      <div className="sp-show-poster" aria-hidden="true">
                        <CardPoster kind={card.poster} />
                      </div>
                    )}
                    <video
                      ref={(el) => {
                        videoRefs.current[i] = el;
                      }}
                      className="sp-show-video"
                      src={card.video}
                      muted
                      playsInline
                      loop={false}
                      autoPlay
                      preload="auto"
                      controls={false}
                      controlsList="nodownload noplaybackrate noremoteplayback nofullscreen"
                      disablePictureInPicture
                      disableRemotePlayback
                      tabIndex={-1}
                      webkit-playsinline="true"
                      x5-playsinline="true"
                      x5-video-player-type="h5-page"
                      onContextMenu={blockVideoInteraction}
                      onClick={blockVideoInteraction}
                      onPointerDown={blockVideoInteraction}
                      onTouchStart={blockVideoInteraction}
                      onCanPlay={(e) =>
                        e.currentTarget.classList.add("is-ready")
                      }
                      onLoadedData={(e) =>
                        e.currentTarget.classList.add("is-ready")
                      }
                      onLoadedMetadata={(e) =>
                        e.currentTarget.classList.add("is-ready")
                      }
                      onError={(e) =>
                        e.currentTarget.classList.remove("is-ready")
                      }
                      onPause={handleVideoPause(i)}
                      onEnded={handleVideoEnded(i)}
                      aria-hidden="true"
                    />

                    {/* Floating capsule: tag — top-left above video */}
                    <span
                      className="sp-cap sp-cap--tag"
                      style={{ animationDelay: "120ms" }}
                      data-testid={`cap-tag-${card.id}`}
                    >
                      <span className={card.tagDotClass} />
                      {card.tag}
                    </span>

                    {/* Floating capsule: metric — top-right above video */}
                    <span
                      className="sp-cap sp-cap--metric"
                      style={{ animationDelay: "240ms" }}
                      data-testid={`cap-metric-${card.id}`}
                    >
                      <span className="sp-cap-metric-value">{card.metric}</span>
                      <span className="sp-cap-metric-label">{card.label}</span>
                    </span>

                    {/* Floating capsule: title — below video */}
                    <div
                      className="sp-cap sp-cap--title"
                      style={{ animationDelay: "360ms", cursor: "default" }}
                      data-testid={`cap-title-${card.id}`}
                    >
                      <span className="sp-cap-title-text">{card.title}</span>
                      <span
                        className="sp-cap-arrow"
                        aria-hidden="true"
                        data-testid={`card-action-${card.id}`}
                      >
                        <ArrowRight
                          size={15}
                          strokeWidth={2.2}
                          color="#1e2430"
                        />
                      </span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
        </div>

        <style>{`
        @media (max-width: 980px) {
          .sp-hero-grid {
            grid-template-columns: 1fr !important;
            text-align: center;
            padding: 40px 8px 80px !important;
          }
          .sp-hero-grid > div:first-child {
            margin: 0 auto;
          }
          .sp-showcase {
            margin: 8px auto 40px;
          }
          [data-testid="hero-curriculum-chips"],
          [data-testid="hero-cta-row"],
          [data-testid="hero-trust-line"] {
            justify-content: center;
          }
        }
      `}</style>
      </section>
    </>
  );
}
